import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

// Components
import { Mui } from 'components';

const HTML_PATTERN = /<\/?[a-z][\s\S]*>/i;
const SAFE_URL_PATTERN = /^(https?:|mailto:|tel:|\/|#)/i;
const UNSAFE_STYLE_PATTERN = /url\(|expression\(|javascript:/i;

const ALLOWED_TAGS = [
  'a',
  'abbr',
  'b',
  'blockquote',
  'br',
  'code',
  'del',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  's',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'u',
  'ul',
  'svg',
  'g',
  'path',
  'circle',
  'ellipse',
  'line',
  'polygon',
  'polyline',
  'rect',
];

const DROPPED_TAGS = [
  'script',
  'style',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'template',
  'noscript',
  'noembed',
  'noframes',
  'xmp',
  'plaintext',
  'title',
  'math',
  'form',
  'input',
  'button',
  'select',
  'textarea',
];

const ALLOWED_ATTRIBUTES = [
  'href',
  'src',
  'alt',
  'title',
  'width',
  'height',
  'style',
  'class',
  'viewbox',
  'xmlns',
  'fill',
  'fill-rule',
  'clip-rule',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'transform',
  'd',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'points',
];

const URL_ATTRIBUTES = ['href', 'src'];

const HEADINGS = '& h1, & h2, & h3, & h4, & h5, & h6';

const isSafeAttribute = (name, value) => {
  if (!ALLOWED_ATTRIBUTES.includes(name)) return false;
  if (URL_ATTRIBUTES.includes(name)) return SAFE_URL_PATTERN.test(value.trim());
  if (name === 'style') return !UNSAFE_STYLE_PATTERN.test(value);
  return true;
};

const sanitizeAttributes = element => {
  Array.from(element.attributes).forEach(({ name, value }) => {
    if (!isSafeAttribute(name.toLowerCase(), value)) element.removeAttribute(name);
  });
};

const sanitizeNode = node => {
  Array.from(node.childNodes).forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) return;
    const tag = child.nodeName.toLowerCase();
    if (child.nodeType !== Node.ELEMENT_NODE || DROPPED_TAGS.includes(tag)) {
      child.remove();
      return;
    }
    sanitizeNode(child);
    if (ALLOWED_TAGS.includes(tag)) sanitizeAttributes(child);
    else child.replaceWith(...child.childNodes);
  });
};

const sanitizeHtml = html => {
  const { body } = new DOMParser().parseFromString(html, 'text/html');
  sanitizeNode(body);
  return body.innerHTML;
};

const useStyles = Mui.makeStyles(theme => ({
  text: { whiteSpace: 'pre-line' },
  html: {
    ...theme.typography.body2,
    overflowWrap: 'anywhere',
    '& > :first-child': { marginTop: 0 },
    '& > :last-child': { marginBottom: 0 },
    '& p': { margin: theme.spacing(0, 0, 0.5) },
    [HEADINGS]: {
      ...theme.typography.subtitle1,
      fontWeight: 600,
      margin: theme.spacing(0, 0, 0.5),
    },
    '& a': { color: 'inherit', fontWeight: 500, textDecoration: 'underline' },
    '& ul, & ol': { margin: theme.spacing(0.5, 0), paddingLeft: theme.spacing(2.5) },
    '& li': { marginBottom: theme.spacing(0.25) },
    '& small': theme.typography.caption,
    '& img, & svg': { maxWidth: '100%', height: 'auto', verticalAlign: 'middle' },
    '& hr': {
      border: 0,
      borderTop: '1px solid currentColor',
      opacity: 0.2,
      margin: theme.spacing(1, 0),
    },
    '& .material-icons': { fontSize: '1.25em', verticalAlign: 'middle' },
  },
}));

function BannerContent({ content, onLinkClick }) {
  // Hooks
  const classes = useStyles();

  // Constants
  const isHtml = typeof content === 'string' && HTML_PATTERN.test(content);
  const sanitizedContent = useMemo(() => (isHtml ? sanitizeHtml(content) : ''), [isHtml, content]);

  // Actions
  const handleClick = event => {
    const anchor = event.target.closest('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (href.startsWith('http') || href.startsWith('/')) event.preventDefault();
    onLinkClick(href);
  };

  if (!content) return null;

  if (!isHtml) return <span className={classes.text}>{content}</span>;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={classes.html}
      onClick={handleClick}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
}

BannerContent.propTypes = {
  content: PropTypes.string,
  onLinkClick: PropTypes.func.isRequired,
};

export default BannerContent;
